import type {
  DocumentTab,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'

export type DocChatPreview = {
  previewDocumentId: string
  previewPage: number
}

type DocChatState = NonNullable<SearchHistoryItem['searchState']['docChat']>

export function getDocChatPreview(
  source:
    | {
        previewDocumentId?: string
        previewPage?: number
      }
    | undefined
): DocChatPreview | undefined {
  const previewDocumentId = source?.previewDocumentId
  if (!previewDocumentId) return undefined
  return {
    previewDocumentId,
    previewPage: source.previewPage ?? 1,
  }
}

/**
 * A just-clicked citation wins over persisted chat preview state. Persisted
 * `previewPage` can still be the previous document's page (often 1) for a
 * render, and treating that as the source of truth drops the cited page and
 * its quotes — Open then lands on page 1 with no highlight.
 */
export function resolveClickedPreviewView(
  clickedPreview: { page: number; citedQuotes?: string[] } | null,
  persistedPreview: Pick<DocChatPreview, 'previewPage'> | undefined
): {
  previewPage: number
  previewCitedQuotes: string[] | undefined
} {
  if (clickedPreview) {
    return {
      previewPage: clickedPreview.page,
      previewCitedQuotes: clickedPreview.citedQuotes,
    }
  }
  return {
    previewPage: persistedPreview?.previewPage ?? 1,
    previewCitedQuotes: undefined,
  }
}

export function getDocChatPreviewFromTab(
  tab: DocumentTab
): DocChatPreview | undefined {
  const item = tab.history[tab.currentIndex]
  if (item.type !== 'search') return undefined
  return getDocChatPreview(item.searchState.docChat)
}

export function withDocChatPreview(
  docChat: DocChatState,
  preview: DocChatPreview | undefined
): DocChatState {
  if (!preview) {
    return {
      ...docChat,
      previewDocumentId: undefined,
      previewPage: undefined,
    }
  }
  return {
    ...docChat,
    previewDocumentId: preview.previewDocumentId,
    previewPage: preview.previewPage,
  }
}

export function searchStateWithDocChatPreview(
  currentSearchState: SearchHistoryItem['searchState'],
  preview: DocChatPreview | null
): Partial<SearchHistoryItem['searchState']> {
  const docChat = currentSearchState.docChat
  if (!docChat) return {}
  return { docChat: withDocChatPreview(docChat, preview ?? undefined) }
}

export function getDocChatArtifactId(
  source: { artifactId?: string } | undefined
): string | undefined {
  const artifactId = source?.artifactId
  return artifactId ? artifactId : undefined
}

/** Opening an artifact clears the document preview so the panel shows the artifact. */
export function searchStateWithDocChatArtifact(
  currentSearchState: SearchHistoryItem['searchState'],
  artifactId: string | null
): Partial<SearchHistoryItem['searchState']> {
  const docChat = currentSearchState.docChat
  if (!docChat) return {}
  if (!artifactId) {
    return { docChat: { ...docChat, artifactId: undefined } }
  }
  return {
    docChat: { ...withDocChatPreview(docChat, undefined), artifactId },
  }
}
